import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Car, TrendingUp, Percent, Star, Power, Tag, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import api, { formatZAR, formatApiErrorDetail } from "../lib/api";
import { vehiclePhoto } from "../lib/photos";

export default function FleetAnalytics({ onChanged }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [percent, setPercent] = useState(10);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/owner/analytics").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const allIds = useMemo(() => (data?.vehicles || []).map((v) => v.id), [data]);
  const allSelected = selected.length > 0 && selected.length === allIds.length;
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const run = async (action, extra = {}) => {
    if (!selected.length) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/owner/vehicles/bulk-action", { vehicle_ids: selected, action, ...extra });
      toast.success(`${res.updated} car${res.updated === 1 ? "" : "s"} updated`);
      setSelected([]);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk action failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="mt-6 text-sm text-[#64748B]">Loading fleet analytics…</div>;

  return (
    <div className="mt-6 space-y-6" data-testid="fleet-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<Car size={18} />} label="Fleet" value={`${data.active}/${data.fleet_size} live`} testId="kpi-fleet" />
        <Kpi icon={<TrendingUp size={18} />} label="Net revenue" value={formatZAR(data.net_zar)} sub={`${data.bookings_confirmed} confirmed bookings`} testId="kpi-revenue" />
        <Kpi icon={<Percent size={18} />} label="Occupancy (30d)" value={`${data.occupancy_30d}%`} sub={`Avg booking ${formatZAR(data.avg_booking_value)}`} testId="kpi-occupancy" />
        <Kpi icon={<Star size={18} />} label="Avg rating" value={data.avg_rating ? data.avg_rating.toFixed(2) : "—"} testId="kpi-rating" />
      </div>

      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
        <div className="flex items-baseline justify-between">
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Revenue & bookings — last 6 months</p>
          <p className="text-xs text-[#64748B]"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#1d7ffd] mr-1.5" />Revenue <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#0a1f4d] ml-3 mr-1.5" />Bookings</p>
        </div>
        <div className="h-64 mt-4" data-testid="fleet-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#EEF2F7" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="r" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `R${Math.round(v / 1000)}k` : `R${v}`)} />
              <YAxis yAxisId="b" orientation="right" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v, n) => (n === "revenue" ? formatZAR(v) : v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Bar yAxisId="r" dataKey="revenue" fill="#1d7ffd" radius={[6, 6, 0, 0]} maxBarSize={44} />
              <Line yAxisId="b" type="monotone" dataKey="bookings" stroke="#0a1f4d" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E2E8F0] flex flex-wrap items-center gap-3">
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B] mr-auto">Per-vehicle performance</p>
          <span className="text-xs text-[#64748B]" data-testid="bulk-selected-count">{selected.length} selected</span>
          <button onClick={() => run("activate")} disabled={!selected.length || busy} className="btn-outline text-xs !py-2 !px-3 disabled:opacity-40" data-testid="bulk-activate"><Power size={12} /> Activate</button>
          <button onClick={() => run("deactivate")} disabled={!selected.length || busy} className="btn-outline text-xs !py-2 !px-3 disabled:opacity-40" data-testid="bulk-deactivate"><Power size={12} /> Pause</button>
          <div className="flex items-center gap-1.5 rounded-full border border-[#E2E8F0] pl-3 pr-1 py-1">
            <Tag size={12} className="text-[#64748B]" />
            <input type="number" value={percent} onChange={(e) => setPercent(Number(e.target.value))} className="w-14 text-xs font-semibold text-[#0a1f4d] outline-none bg-transparent" data-testid="bulk-percent-input" />
            <span className="text-xs text-[#64748B]">%</span>
            <button onClick={() => run("adjust_price", { percent })} disabled={!selected.length || busy} className="text-xs font-bold text-white bg-[#0a1f4d] rounded-full px-3 py-1.5 disabled:opacity-40" data-testid="bulk-adjust-price">Adjust price</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fleet-table">
            <thead>
              <tr className="text-left text-[11px] tracking-[0.14em] uppercase text-[#64748B] bg-[#F7F8FA]">
                <th className="px-5 py-3 w-10">
                  <button onClick={() => setSelected(allSelected ? [] : allIds)} aria-label="Select all" data-testid="bulk-select-all" className="text-[#0a1f4d]">
                    {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                  </button>
                </th>
                <th className="px-3 py-3">Vehicle</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3 text-right">Price/day</th>
                <th className="px-3 py-3 text-right">Bookings</th>
                <th className="px-3 py-3 text-right">Days booked</th>
                <th className="px-3 py-3 text-right">Occupancy 30d</th>
                <th className="px-3 py-3 text-right">Revenue</th>
                <th className="px-5 py-3 text-right">Rating</th>
              </tr>
            </thead>
            <tbody>
              {data.vehicles.map((v) => (
                <tr key={v.id} className={`border-t border-[#EEF2F7] ${selected.includes(v.id) ? "bg-[#1d7ffd]/5" : ""}`} data-testid={`fleet-row-${v.id}`}>
                  <td className="px-5 py-3">
                    <button onClick={() => toggle(v.id)} aria-label="Select" data-testid={`fleet-select-${v.id}`} className="text-[#0a1f4d]">
                      {selected.includes(v.id) ? <CheckSquare size={16} className="text-[#1d7ffd]" /> : <Square size={16} />}
                    </button>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3 min-w-[200px]">
                      <img src={vehiclePhoto({ photos: v.photo ? [v.photo] : [], vehicle_type: v.vehicle_type })} alt="" className="w-12 h-9 rounded-lg object-cover bg-[#F0F2F5]" />
                      <div className="min-w-0">
                        <p className="font-semibold text-[#0a1f4d] truncate">{v.brand} {v.model}</p>
                        <p className="text-[11px] text-[#64748B]">{v.city}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${v.is_active ? "bg-[#10B981]/15 text-[#065F46]" : "bg-[#64748B]/15 text-[#475569]"}`}>{v.is_active ? "Live" : "Paused"}</span>
                  </td>
                  <td className="px-3 py-3 text-right font-semibold text-[#0a1f4d]" data-testid={`fleet-price-${v.id}`}>{formatZAR(v.price_per_day)}</td>
                  <td className="px-3 py-3 text-right text-[#0a1f4d]">{v.bookings}</td>
                  <td className="px-3 py-3 text-right text-[#0a1f4d]">{v.days_booked}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <span className="w-16 h-1.5 rounded-full bg-[#F0F2F5] overflow-hidden"><span className="block h-full bg-[#1d7ffd]" style={{ width: `${v.occupancy_30d}%` }} /></span>
                      <span className="text-[#0a1f4d] w-10">{v.occupancy_30d}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right font-bold text-[#0a1f4d]">{formatZAR(v.revenue)}</td>
                  <td className="px-5 py-3 text-right text-[#0a1f4d]">{v.rating_count ? `${v.rating_avg} (${v.rating_count})` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ icon, label, value, sub, testId }) => (
  <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white" data-testid={testId}>
    <div className="text-[#1d7ffd]">{icon}</div>
    <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B] mt-3">{label}</p>
    <p className="font-display font-black mt-1 text-2xl text-[#0a1f4d]">{value}</p>
    {sub && <p className="text-[11px] text-[#64748B] mt-1">{sub}</p>}
  </div>
);
